"use client"
import React, { useState, useEffect, useCallback } from "react"
import { Card, Row, Col, Typography, Statistic, Spin, message, Button } from "antd"
import { LeftOutlined, RightOutlined, TrophyOutlined, ReloadOutlined, LockOutlined } from "@ant-design/icons"
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts"
import styled from "styled-components"
import { useDispatch, useSelector } from "react-redux"

// Import Redux actions and selectors
import {
  fetchActivityMap,
  setActivityDateRange,
  fetchCompletedPracticeAssessments,
  fetchPracticeScores,
  fetchAptitudePracticeScores,
} from "../../actions"
import {
  selectActivityData,
  selectActivityLoading,
  selectActivityError,
  selectActivityDateRange,
  selectPracticeScores,
  selectPracticeScoresLoading,
  selectPracticeScoresError,
  selectCompletedPracticeAssessments,
  selectCompletedPracticeAssessmentsLoading,
  selectCompletedPracticeAssessmentsPagination,
  selectAptitudePracticeScores2,
  selectAptitudePracticeScoresLoading,
  selectAptitudePracticeScoresError,
  selectCommunicationDayZero,
  selectAptitudeDayZero,
} from "../../selectors"

// Demo chart data for preview - Enhanced with more realistic progression
const demoChartData = [
  { session: 0, "Communication Progress": 0, "Aptitude Progress": 0 },
  { session: 1, "Communication Progress": 18, "Aptitude Progress": 15 },
  { session: 2, "Communication Progress": 32, "Aptitude Progress": 28 },
  { session: 3, "Communication Progress": 45, "Aptitude Progress": 38 },
  { session: 4, "Communication Progress": 58, "Aptitude Progress": 52 },
  { session: 5, "Communication Progress": 68, "Aptitude Progress": 63 },
  { session: 6, "Communication Progress": 76, "Aptitude Progress": 71 },
  { session: 7, "Communication Progress": 82, "Aptitude Progress": 78 },
  { session: 8, "Communication Progress": 87, "Aptitude Progress": 84 },
]

// Central CTA Card Components
const CentralCTAOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
  padding: 20px;
  pointer-events: none;
`

const CentralCTACard = styled.div`
  background: #ffffff;
  border-radius: 16px;
  padding: 40px 32px;
  box-shadow: 0 12px 48px rgba(0, 0, 0, 0.15);
  border: 1px solid #e8e8e8;
  text-align: center;
  max-width: 480px;
  width: 100%;
  pointer-events: all;
  backdrop-filter: none;
  
  @media (max-width: 768px) {
    padding: 32px 24px;
    max-width: 400px;
  }
  
  @media (max-width: 480px) {
    padding: 24px 20px;
    max-width: 320px;
    margin: 0 16px;
  }
`

const CTAIcon = styled.div`
  width: 64px;
  height: 64px;
  background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px auto;
  box-shadow: 0 8px 24px rgba(24, 144, 255, 0.3);
  
  .anticon {
    font-size: 28px;
    color: #ffffff;
  }
  
  @media (max-width: 480px) {
    width: 56px;
    height: 56px;
    margin-bottom: 20px;
    
    .anticon {
      font-size: 24px;
    }
  }
`

const CTATitle = styled.h2`
  font-size: 24px;
  font-weight: 700;
  color: #262626;
  margin: 0 0 16px 0;
  font-family: 'Plus Jakarta Sans', sans-serif;
  letter-spacing: 0.3px;
  
  @media (max-width: 768px) {
    font-size: 22px;
  }
  
  @media (max-width: 480px) {
    font-size: 20px;
    margin-bottom: 12px;
  }
`

const CTADescription = styled.p`
  font-size: 16px;
  color: #595959;
  margin: 0 0 32px 0;
  line-height: 1.6;
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 400;
  
  @media (max-width: 768px) {
    font-size: 15px;
    margin-bottom: 28px;
  }
  
  @media (max-width: 480px) {
    font-size: 14px;
    margin-bottom: 24px;
    line-height: 1.5;
  }
`

const CTAButton = styled(Button)`
  height: 48px;
  padding: 0 32px;
  font-size: 16px;
  font-weight: 600;
  border-radius: 8px;
  background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%);
  border: none;
  box-shadow: 0 4px 16px rgba(24, 144, 255, 0.3);
  
  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #40a9ff 0%, #1890ff 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(24, 144, 255, 0.4);
  }
  
  &:focus {
    background: linear-gradient(135deg, #1890ff 0%, #40a9ff 100%);
  }
  
  @media (max-width: 480px) {
    height: 44px;
    padding: 0 24px;
    font-size: 15px;
  }
`

// Blur Wrapper Component
const BlurWrapper = styled.div`
  filter: blur(5px);
  opacity: 0.6;
  pointer-events: none;
  user-select: none;
  transition: all 0.3s ease;
`

// Enhanced responsive styled components with additional fine-tuning
const ActivityContentWrapper = styled.div`
  position: relative;
  width: 100%;

  .activity-content {
    width: 100%;
    transition: all 0.3s ease;
  }
`

const ProgressContentWrapper = styled.div`
  position: relative;
  width: 100%;

  .progress-content {
    width: 100%;
    transition: all 0.3s ease;
  }
`

const OverlayContainer = styled.div`
  position: relative;

  &.locked {
    pointer-events: none;
    user-select: none;
  }
`

// Stats Container with unified overlay support
const StatsContainer = styled.div`
  position: relative;
  width: 100%;

  &.empty-state {
    .ant-card {
      filter: blur(2px);
      opacity: 0.6;
      transition: all 0.3s ease;
    }
  }
`

// Error Boundary Component
class ChartErrorBoundary extends React.Component {
  constructor(props) {
    super(props)
    this.state = { hasError: false }
  }

  static getDerivedStateFromError(error) {
    return { hasError: true }
  }

  componentDidCatch(error, errorInfo) {
    console.error("Chart rendering error:", error, errorInfo)
  }

  render() {
    if (this.state.hasError) {
      return (
        <EmptyWrapper>
          <div className="empty-title">Chart rendering error</div>
          <div className="empty-description">Unable to display progress chart. Please try refreshing the page.</div>
        </EmptyWrapper>
      )
    }
    return this.props.children
  }
}

const { Title, Text } = Typography

// Professional styled components with light color scheme
const SectionWrapper = styled.div`
  padding: 24px;
  min-height: 100vh;
  position: relative;
`

const SummaryCardsRow = styled(Row)`
  margin-bottom: 24px;
  @media (max-width: 768px) {
    margin-bottom: 20px;
  }
`

const SummaryCard = styled(Card)`
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  background: #ffffff;
  transition: all 0.2s ease;
  height: 120px;
  &:hover {
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
    border-color: #d9d9d9;
  }
  .ant-card-body {
    padding: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
  }
  .ant-statistic {
    width: 100%;
    text-align: center;
  }
  .ant-statistic-title {
    font-size: 14px;
    color: #595959;
    font-weight: 700;
    margin-bottom: 8px;
    text-align: center;
    letter-spacing: 0.8px;
    line-height: 1.3;
    display: block;
  }
  .ant-statistic-content {
    font-weight: 800;
    text-align: center;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  .ant-statistic-content-value {
    font-size: 30px;
    font-weight: 800;
    color: #262626;
    text-align: center;
    line-height: 1.2;
  }
  .ant-statistic-content-suffix {
    font-size: 14px;
    font-weight: 700;
    color: #595959;
    margin-left: 6px;
    text-align: center;
  }
`

// Activity Tracker Styles - Refined Responsive Layout
const ActivityCard = styled(Card)`
  border-radius: 16px;
  border: 1px solid #e8e8e8;
  background: #ffffff;
  margin-bottom: 32px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.06);
  overflow: hidden;
  transition: all 0.3s ease;

  &:hover {
    box-shadow: 0 6px 28px rgba(0, 0, 0, 0.08);
  }

  .ant-card-head {
    border-bottom: 1px solid #f0f0f0;
    background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
    padding: 20px 24px;
    text-align: center;
    transition: all 0.3s ease;
  }
  .ant-card-head-title {
    color: #262626 !important;
    margin-bottom: 0 !important;
    font-size: 20px !important;
    font-weight: 600 !important;
    letter-spacing: 0.3px;
  }
  .ant-card-body {
    padding: 24px;
    background: #ffffff;
    position: relative;
    @media (max-width: 768px) {
      padding: 20px;
    }
  }
`

// Centered Container Wrapper
const TrackerContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 16px;
  width: 100%;
  @media (max-width: 768px) {
    padding: 0 12px;
  }
`

const MonthNavigator = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  gap: 20px;
  padding: 16px 24px;
  background: linear-gradient(135deg, #f8f9fa 0%, #f1f3f4 100%);
  border-radius: 16px;
  border: 1px solid #e9ecef;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.04);
  max-width: 400px;
  margin-left: auto;
  margin-right: auto;
  @media (max-width: 768px) {
    padding: 14px 20px;
    gap: 16px;
    max-width: 350px;
  }
  @media (max-width: 480px) {
    padding: 12px 18px;
    gap: 14px;
    margin-bottom: 20px;
  }
`

const MonthButton = styled(Button)`
  border: 1px solid #d9d9d9;
  background: #ffffff;
  color: #595959;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  &:hover:not(:disabled) {
    border-color: #40a9ff;
    color: #40a9ff;
    background: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(64, 169, 255, 0.2);
  }
  &:focus {
    border-color: #40a9ff;
    color: #40a9ff;
  }
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  }
  .anticon {
    font-size: 14px;
  }
  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    border-radius: 8px;
    
    .anticon {
      font-size: 12px;
    }
  }
`

const MonthTitle = styled.div`
  font-size: 18px;
  font-weight: 700;
  color: #262626;
  min-width: 180px;
  text-align: center;
  letter-spacing: 0.3px;
  @media (max-width: 768px) {
    font-size: 16px;
    min-width: 160px;
  }
  @media (max-width: 480px) {
    font-size: 15px;
    min-width: 140px;
  }
`

// Optimized Flex Layout with Reduced Gap and Vertical Centering
const ActivityTrackerLayout = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  justify-content: evenly;
  width: 100%;
  @media (max-width: 992px) {
    gap: 14px;
  }
  @media (max-width: 768px) {
    flex-direction: column;
    gap: 20px;
    align-items: center;
  }
`

const CalendarPanel = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 3;
  width: 100%;
  max-width: 700px;
  position: relative;
  @media (max-width: 768px) {
    flex: none;
    max-width: 100%;
    order: 2;
  }
`

// Enhanced Loading Overlay for Calendar
const CalendarLoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.85);
  backdrop-filter: blur(4px);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  z-index: 10;
  opacity: ${(props) => (props.show ? 1 : 0)};
  visibility: ${(props) => (props.show ? "visible" : "hidden")};
  transition: all 0.3s ease;
  .loading-spinner {
    .ant-spin-dot {
      font-size: 24px;
    }
  }
  .loading-text {
    color: #595959;
    font-size: 15px;
    font-weight: 600;
    letter-spacing: 0.2px;
    text-align: center;
  }
  @media (max-width: 480px) {
    .loading-spinner {
      .ant-spin-dot {
        font-size: 20px;
      }
    }
    .loading-text {
      font-size: 14px;
    }
  }
`

// Month Loading State Wrapper
const MonthNavigatorWrapper = styled.div`
  position: relative;
  z-index: 5;
  margin-bottom: 24px;
  @media (max-width: 480px) {
    margin-bottom: 20px;
  }
`

const SummaryPanel = styled.div`
  display: flex;
  flex-direction: column;
  flex: 2;
  width: 100%;
  max-width: 350px;
  align-self: center;
  position: sticky;
  top: 20px;
  @media (max-width: 768px) {
    position: static;
    flex: none;
    max-width: 400px;
    order: 1;
    align-self: stretch;
  }
`

// Compact Calendar Container
const CalendarContainer = styled.div`
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 16px;
  padding: 24px;
  border: 1px solid #e9ecef;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.06);
  width: 100%;
  max-width: 480px;
  margin: 0 auto;
  @media (max-width: 768px) {
    padding: 20px;
    max-width: 100%;
  }
  @media (max-width: 480px) {
    padding: 16px;
    border-radius: 12px;
  }
`

const WeekdaysGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  margin-bottom: 16px;
  width: 100%;
  @media (max-width: 480px) {
    gap: 4px;
    margin-bottom: 12px;
  }
`

const WeekdayLabel = styled.div`
  font-size: 11px;
  color: #8c8c8c;
  font-weight: 700;
  text-align: center;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  background: linear-gradient(135deg, #f8f9fa 0%, #f1f3f4 100%);
  border-radius: 6px;
  border: 1px solid #e9ecef;
  @media (max-width: 480px) {
    font-size: 10px;
    height: 20px;
    border-radius: 4px;
  }
`

const ActivityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 6px;
  margin-bottom: 20px;
  width: 100%;
  @media (max-width: 480px) {
    gap: 4px;
    margin-bottom: 16px;
  }
`

const ActivityDay = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 10px;
  border: 2px solid
    ${(props) => {
      if (props.selected) return "#1890ff"
      if (props.active) return "#52c41a"
      return "#e8e8e8"
    }};
  background: ${(props) => {
    if (props.selected) {
      return "linear-gradient(135deg, #e6f7ff 0%, #bae7ff 100%)"
    }
    if (props.active) {
      return "linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%)"
    }
    return "linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)"
  }};
  color: ${(props) => {
    if (props.selected) return "#1890ff"
    if (props.active) return "#389e0d"
    return "#bfbfbf"
  }};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: ${(props) => (props.active || props.selected ? "700" : "500")};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: ${(props) => (props.children ? "pointer" : "default")};
  position: relative;
  box-shadow: ${(props) => {
    if (props.selected) return "0 4px 12px rgba(24, 144, 255, 0.2)"
    if (props.active) return "0 4px 12px rgba(82, 196, 26, 0.2)"
    return "0 2px 6px rgba(0, 0, 0, 0.06)"
  }};
  &:hover {
    ${(props) =>
      props.children &&
      `
      transform: translateY(-2px) scale(1.05);
      box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
      z-index: 2;
    `}
  }
  @media (max-width: 480px) {
    width: 36px;
    height: 36px;
    font-size: 11px;
    border-radius: 8px;
  }
  @media (max-width: 360px) {
    width: 32px;
    height: 32px;
    font-size: 10px;
    border-radius: 6px;
  }
`

const TrophyIcon = styled.div`
  position: absolute;
  top: 2px;
  right: 2px;
  font-size: 10px;
  color: #faad14;
  .anticon {
    filter: drop-shadow(0 1px 2px rgba(250, 173, 20, 0.4));
  }
  @media (max-width: 480px) {
    font-size: 9px;
    top: 1px;
    right: 1px;
  }
`

const LegendContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
  padding: 14px 18px;
  background: linear-gradient(135deg, #f8f9fa 0%, #f1f3f4 100%);
  border-radius: 12px;
  border: 1px solid #e9ecef;
  font-size: 12px;
  color: #595959;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.04);
  @media (max-width: 480px) {
    flex-direction: column;
    gap: 12px;
    font-size: 11px;
    padding: 12px 16px;
  }
`

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: 600;
  .legend-dot {
    width: 12px;
    height: 12px;
    border-radius: 3px;
    border: 2px solid #52c41a;
    background: linear-gradient(135deg, #f6ffed 0%, #d9f7be 100%);
    box-shadow: 0 2px 4px rgba(82, 196, 26, 0.2);
  }
  .legend-trophy {
    color: #faad14;
    font-size: 14px;
    filter: drop-shadow(0 1px 2px rgba(250, 173, 20, 0.4));
  }
`

// Compact Summary Card
const ActivitySummaryCard = styled.div`
  background: #ffffff;
  border: 1px solid #e8e8e8;
  border-radius: 16px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  overflow: hidden;
  height: fit-content;
  width: 100%;
  &:hover {
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    transform: translateY(-2px);
  }
  @media (max-width: 768px) {
    border-radius: 12px;
  }
`

const SummaryHeader = styled.div`
  padding: 20px 24px 16px 24px;
  background: linear-gradient(135deg, #f8f9fa 0%, #f1f3f4 100%);
  border-bottom: 1px solid #e8e8e8;
  text-align: center;
  @media (max-width: 768px) {
    padding: 18px 20px 14px 20px;
  }
  @media (max-width: 480px) {
    padding: 16px 18px 12px 18px;
  }
`

const SummaryDate = styled.h3`
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: #262626;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  letter-spacing: 0.2px;
  @media (max-width: 768px) {
    font-size: 15px;
  }
  @media (max-width: 480px) {
    font-size: 14px;
  }
`

const SummaryContent = styled.div`
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 200px;
  @media (max-width: 768px) {
    padding: 20px;
    min-height: 180px;
  }
  @media (max-width: 480px) {
    padding: 18px;
    min-height: 160px;
    gap: 14px;
  }
`

const ActivityItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 14px 18px;
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
  border-radius: 10px;
  border: 1px solid #f0f0f0;
  transition: all 0.3s ease;
  &:hover {
    background: linear-gradient(135deg, #f0f0f0 0%, #e8e8e8 100%);
    transform: translateX(4px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
  }
  @media (max-width: 480px) {
    padding: 12px 16px;
  }
`

const ActivityLabel = styled.span`
  font-size: 13px;
  color: #595959;
  font-weight: 600;
  letter-spacing: 0.1px;
  @media (max-width: 480px) {
    font-size: 12px;
  }
`

const ActivityValue = styled.span`
  font-size: 15px;
  color: #262626;
  font-weight: 700;
  background: linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%);
  padding: 6px 14px;
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  min-width: 36px;
  text-align: center;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  @media (max-width: 480px) {
    font-size: 14px;
    padding: 5px 12px;
    min-width: 32px;
  }
`

const AchievementBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  background: linear-gradient(135deg, #fff7e6 0%, #ffecc7 100%);
  border: 2px solid #faad14;
  border-radius: 12px;
  padding: 16px;
  margin-top: 16px;
  box-shadow: 0 4px 12px rgba(250, 173, 20, 0.15);
  .anticon {
    color: #faad14;
    font-size: 18px;
    filter: drop-shadow(0 2px 4px rgba(250, 173, 20, 0.3));
  }
  span {
    font-size: 14px;
    font-weight: 700;
    color: #d48806;
    letter-spacing: 0.2px;
  }
  @media (max-width: 480px) {
    padding: 14px;
    margin-top: 14px;
    gap: 10px;
    
    .anticon {
      font-size: 16px;
    }
    
    span {
      font-size: 13px;
    }
  }
`

const NoActivityMessage = styled.div`
  text-align: center;
  color: #8c8c8c;
  font-size: 14px;
  font-weight: 500;
  padding: 40px 20px;
  background: linear-gradient(135deg, #fafafa 0%, #f5f5f5 100%);
  border-radius: 12px;
  border: 2px dashed #d9d9d9;
  margin: 16px 0;
  line-height: 1.5;
  @media (max-width: 480px) {
    padding: 32px 16px;
    font-size: 13px;
    margin: 12px 0;
  }
`

const ProgressCard = styled(Card)`
  border-radius: 8px;
  border: 1px solid #e8e8e8;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  transition: all 0.3s ease;

  .ant-card-head {
    border-bottom: 1px solid #f0f0f0;
    background: #fafafa;
    transition: all 0.3s ease;
  }
  .ant-card-head-title {
    color: #262626 !important;
    margin-bottom: 0 !important;
    font-size: 20px !important;
    font-weight: 600 !important;
  }
  .ant-card-body {
    padding: 24px;
    position: relative;
  }
`

const ChartContainer = styled.div`
  margin-bottom: 20px;
  width: 100%;
  height: 300px;
  background: #ffffff;
  border-radius: 6px;
  border: 1px solid #f0f0f0;
  padding: 16px;
`

const LoadingWrapper = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 120px;
  flex-direction: column;
  gap: 12px;
  .loading-text {
    color: #8c8c8c;
    font-size: 14px;
  }
`

const ErrorWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 20px;
  min-height: 200px;
  justify-content: center;
  .error-title {
    color: #ff4d4f;
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 8px;
  }
  .error-description {
    color: #8c8c8c;
    font-size: 14px;
    margin-bottom: 16px;
  }
`

const EmptyWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 40px 20px;
  min-height: 200px;
  justify-content: center;
  .empty-title {
    color: #8c8c8c;
    font-size: 16px;
    font-weight: 500;
    margin-bottom: 8px;
  }
  .empty-description {
    color: #bfbfbf;
    font-size: 14px;
  }
`

// Create a custom selector that uses current date
const useFormattedActivityData = () => {
  const activityData = useSelector(selectActivityData)
  const [currentDate, setCurrentDate] = useState(new Date())

  return React.useMemo(() => {
    // Create a map of active dates with full data
    const activeDatesMap = {}
    activityData.forEach((item) => {
      activeDatesMap[item.Date] = {
        isActive: true,
        hasAchievement: item.is_trophy,
        cefr: item.Cefr,
        typeCounts: item.typeCounts || {
          communication: 0,
          aptitude: 0,
          behavior: 0,
          technical: 0,
        },
      }
    })

    // Generate calendar grid (42 days to fill 6 weeks)
    const currentMonth = currentDate.getMonth()
    const currentYear = currentDate.getFullYear()

    // Get first day of month and calculate starting position
    const firstDay = new Date(currentYear, currentMonth, 1)
    const startingDayOfWeek = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1 // Monday = 0

    // Get number of days in current month
    const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate()

    // Fill the grid with 42 days (6 rows × 7 days)
    const formattedData = []
    for (let i = 0; i < 42; i++) {
      if (i < startingDayOfWeek) {
        // Empty cells before month starts
        formattedData.push({
          day: null,
          isActive: false,
          hasAchievement: false,
          cefr: null,
          typeCounts: {
            communication: 0,
            aptitude: 0,
            behavior: 0,
            technical: 0,
          },
        })
      } else if (i - startingDayOfWeek + 1 <= daysInMonth) {
        // Days of current month
        const day = i - startingDayOfWeek + 1
        const dateStr = `${currentYear}-${String(currentMonth + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`
        const activityInfo = activeDatesMap[dateStr] || {
          isActive: false,
          hasAchievement: false,
          cefr: null,
          typeCounts: {
            communication: 0,
            aptitude: 0,
            behavior: 0,
            technical: 0,
          },
        }

        formattedData.push({
          day,
          ...activityInfo,
        })
      } else {
        // Empty cells after month ends
        formattedData.push({
          day: null,
          isActive: false,
          hasAchievement: false,
          cefr: null,
          typeCounts: {
            communication: 0,
            aptitude: 0,
            behavior: 0,
            technical: 0,
          },
        })
      }
    }

    return { formattedData, setCurrentDate }
  }, [activityData, currentDate])
}

const Overview = () => {
  const dispatch = useDispatch()

  // Redux selectors
  const { formattedData: activityData, setCurrentDate: setActivityCurrentDate } = useFormattedActivityData()
  const activityLoading = useSelector(selectActivityLoading)
  const activityError = useSelector(selectActivityError)
  const dateRange = useSelector(selectActivityDateRange)

  // Practice scores selectors
  const practiceScores = useSelector(selectPracticeScores)
  const practiceScoresLoading = useSelector(selectPracticeScoresLoading)
  const practiceScoresError = useSelector(selectPracticeScoresError)
  const aptitudePracticeScores = useSelector(selectAptitudePracticeScores2)
  const aptitudePracticeScoresLoading = useSelector(selectAptitudePracticeScoresLoading)
  const aptitudePracticeScoresError = useSelector(selectAptitudePracticeScoresError)

  // DayZero selectors
  const communicationDayZero = useSelector(selectCommunicationDayZero)
  const aptitudeDayZero = useSelector(selectAptitudeDayZero)

  // Completed practice assessments selectors
  const completedPracticeAssessments = useSelector(selectCompletedPracticeAssessments)
  const completedPracticeAssessmentsLoading = useSelector(selectCompletedPracticeAssessmentsLoading)
  const completedPracticeAssessmentsPagination = useSelector(selectCompletedPracticeAssessmentsPagination)

  // Add practice access selectors
  const practiceAccessState = useSelector((state) => state.assessment?.practiceAccess)
  const communicationAccess = practiceAccessState?.Communication || {
    hasAccess: false,
    completedCount: 0,
    requiredCount: 2,
  }
  const aptitudeAccess = practiceAccessState?.Aptitude || { hasAccess: false, completedCount: 0, requiredCount: 2 }

  // Check if user has any practice access
  const hasAnyPracticeAccess = communicationAccess.hasAccess || aptitudeAccess.hasAccess
  const hasNoPracticeAccess = !communicationAccess.hasAccess && !aptitudeAccess.hasAccess

  // Local state for dashboard data
  const [dashboardData, setDashboardData] = useState({
    totalPracticed: 0,
    communicationSessions: 0,
    aptitudeSessions: 0,
    technicalSessions: 0,
  })
  const [currentDate, setCurrentDate] = useState(new Date())
  const [retryLoading, setRetryLoading] = useState(false)
  const [monthNavigationLoading, setMonthNavigationLoading] = useState(false)

  // Add selectors for student data
  const studentDetails = useSelector((state) => state.studentData?.studentDetails)
  const authStudentEmail = studentDetails?.admin?.email
  const authStudentId = studentDetails?.id

  // Generate chart data function - Modified to use demo data when locked
  const generateChartData = useCallback(() => {
  // If user has no practice access, return demo data for preview
  if (hasNoPracticeAccess) {
    return demoChartData.map((item, index) => ({
      session: index,
      communication_combined_progress: item["Communication Progress"],
      aptitude_combined_progress: item["Aptitude Progress"],
      submittedAt: new Date(Date.now() - (demoChartData.length - index) * 24 * 60 * 60 * 1000).toISOString(),
      assessmentAssignedId: `demo_${index}`,
      type: index % 2 === 0 ? "communication" : "aptitude",
    }))
  }

  // Add proper null checks and ensure arrays for real data
  const communicationScores = Array.isArray(practiceScores?.data) ? practiceScores.data : 
                               Array.isArray(practiceScores) ? practiceScores : []
  const aptitudeScores = Array.isArray(aptitudePracticeScores?.data) ? aptitudePracticeScores.data : 
                         Array.isArray(aptitudePracticeScores) ? aptitudePracticeScores : []

  // Create a map of all unique dates with their respective scores
  const dateScoresMap = new Map()

  // Add communication scores by date
  communicationScores.forEach((scoreData) => {
    if (scoreData && scoreData.submittedAt) {
      const date = scoreData.submittedAt
      if (!dateScoresMap.has(date)) {
        dateScoresMap.set(date, {
          date,
          communicationScore: 0,
          aptitudeScore: 0,
          hasCommunication: false,
          hasAptitude: false
        })
      }
      const entry = dateScoresMap.get(date)
      entry.communicationScore = scoreData.total_score || 0
      entry.hasCommunication = true
      entry.communicationDelta = scoreData.delta || 0
    }
  })

  // Add aptitude scores by date
  aptitudeScores.forEach((scoreData) => {
    if (scoreData && scoreData.submittedAt) {
      const date = scoreData.submittedAt
      if (!dateScoresMap.has(date)) {
        dateScoresMap.set(date, {
          date,
          communicationScore: 0,
          aptitudeScore: 0,
          hasCommunication: false,
          hasAptitude: false
        })
      }
      const entry = dateScoresMap.get(date)
      entry.aptitudeScore = scoreData.total_score || 0
      entry.hasAptitude = true
      entry.aptitudeDelta = scoreData.delta || 0
    }
  })

  // Sort by date and create sessions
  const sortedDates = Array.from(dateScoresMap.values()).sort(
    (a, b) => new Date(a.date) - new Date(b.date)
  )

  const sessions = []

  // Start with session 0 (baseline with dayZero data)
  sessions.push({
    session: 0,
    communication_combined_progress: communicationDayZero?.total_score || 0,
    aptitude_combined_progress: aptitudeDayZero?.total_score || 0,
    submittedAt: null,
    assessmentAssignedId: null,
    delta: 0,
    type: 'dayZero'
  })

  // Process each unique date - directly use backend scores without EMA
  sortedDates.forEach((dateEntry, index) => {
    // Create session entry with direct scores from backend
    sessions.push({
      session: index + 1,
      communication_combined_progress: dateEntry.hasCommunication ? dateEntry.communicationScore : 
                                      (sessions[sessions.length - 1]?.communication_combined_progress || 0),
      aptitude_combined_progress: dateEntry.hasAptitude ? dateEntry.aptitudeScore : 
                                 (sessions[sessions.length - 1]?.aptitude_combined_progress || 0),
      submittedAt: dateEntry.date,
      assessmentAssignedId: null,
      type: dateEntry.hasCommunication && dateEntry.hasAptitude ? 'both' : 
            dateEntry.hasCommunication ? 'communication' : 'aptitude',
      delta: dateEntry.hasCommunication ? dateEntry.communicationDelta : 
             dateEntry.hasAptitude ? dateEntry.aptitudeDelta : 0
    })
  })

  return sessions
}, [practiceScores, aptitudePracticeScores, hasNoPracticeAccess, communicationDayZero, aptitudeDayZero])

  // Add this function for chart data preparation - Modified to handle demo data
  const prepareChartData = useCallback(() => {
    try {
      const chartData = generateChartData()
      return chartData.map((session) => ({
        session: session.session,
        "Communication Progress": session.communication_combined_progress || 0,
        "Aptitude Progress": session.aptitude_combined_progress || 0,
      }))
    } catch (error) {
      console.error("Error preparing chart data:", error)
      return []
    }
  }, [generateChartData])

  // Update the chart rendering section
  const isLoading = practiceScoresLoading || aptitudePracticeScoresLoading
  const hasError = practiceScoresError || aptitudePracticeScoresError
  const errorMessage = practiceScoresError || aptitudePracticeScoresError

  // Check if components should show empty states - Modified for demo data
  const hasChartData = generateChartData().length > 0
  const hasActivityData = activityData.some((cell) => cell.isActive)
  const hasSummaryData = dashboardData.totalPracticed > 0

  // Add selected date state management with improved logic
  const [selectedDate, setSelectedDate] = useState(null) // Start with no selection
  const [selectedDateData, setSelectedDateData] = useState(null)

  // Helper function to check if we're viewing the current month
  const isCurrentMonth = useCallback(() => {
    const today = new Date()
    return currentDate.getFullYear() === today.getFullYear() && currentDate.getMonth() === today.getMonth()
  }, [currentDate])

  // Helper function to get today's date number
  const getTodayDate = useCallback(() => {
    return new Date().getDate()
  }, [])

  // Enhanced date selection handler
  const handleDateSelect = (cell) => {
    if (cell.day) {
      setSelectedDate(cell.day)
      // Create selected date data object using REAL API data
      const dateData = {
        date: new Date(currentDate.getFullYear(), currentDate.getMonth(), cell.day),
        isActive: cell.isActive,
        hasAchievement: cell.hasAchievement,
        cefr: cell.cefr,
        communicationCount: cell.typeCounts?.communication || 0,
        aptitudeCount: cell.typeCounts?.aptitude || 0,
        behaviorCount: cell.typeCounts?.behavior || 0,
      }
      setSelectedDateData(dateData)
    }
  }

  // Format selected date for display
  const formatSelectedDate = (date) => {
    return date.toLocaleDateString("en-US", {
      day: "numeric",
      month: "long",
      year: "numeric",
    })
  }

  // Enhanced month navigation with improved date selection logic
  const navigateMonth = async (direction) => {
    if (monthNavigationLoading) return
    setMonthNavigationLoading(true)

    try {
      const newDate = new Date(currentDate)
      newDate.setMonth(currentDate.getMonth() + direction)
      setCurrentDate(newDate)
      setActivityCurrentDate(newDate)

      // Clear selection when navigating to different months
      setSelectedDate(null)
      setSelectedDateData(null)

      // Fetch new month data
      const { startDate, endDate } = getCurrentMonthRange(newDate)
      if (authStudentEmail) {
        await dispatch(fetchActivityMap(authStudentEmail, true, startDate, endDate))
      }
    } catch (error) {
      showErrorMessage(error, "Failed to load month data")
    } finally {
      setTimeout(() => {
        setMonthNavigationLoading(false)
      }, 300)
    }
  }

  // Smart initialization - only auto-select today when viewing current month
  useEffect(() => {
    if (activityData.length > 0) {
      if (isCurrentMonth()) {
        // Auto-select today's date only when viewing current month
        const today = getTodayDate()
        const todayCell = activityData.find((cell) => cell.day === today)
        if (todayCell) {
          handleDateSelect(todayCell)
        }
      } else {
        // Clear selection when viewing other months
        setSelectedDate(null)
        setSelectedDateData(null)
      }
    }
  }, [activityData, currentDate, isCurrentMonth, getTodayDate])

  // Enhanced ActivityDayCell with improved selection logic
  const ActivityDayCell = ({ day, isActive, hasAchievement, cefr, isSelected, onClick }) => {
    // Add visual indicator for today's date when viewing current month
    const isToday = isCurrentMonth() && day === getTodayDate()

    return (
      <ActivityDay
        active={isActive && day}
        selected={isSelected}
        onClick={onClick}
        title={day ? `Day ${day}${cefr ? ` - ${cefr}` : ""}${isToday ? " (Today)" : ""}` : ""}
        style={{
          // Add subtle border for today's date when not selected
          border: isToday && !isSelected ? "2px solid #40a9ff" : undefined,
        }}
      >
        {day ? (
          <>
            {day}
            {hasAchievement && isActive && (
              <TrophyIcon>
                <TrophyOutlined />
              </TrophyIcon>
            )}
          </>
        ) : (
          ""
        )}
      </ActivityDay>
    )
  }

  // Enhanced error handling functions
  const showErrorMessage = (error, context = "") => {
    const errorMessage = error?.message || error?.toString() || "An unexpected error occurred"
    message.error(`${context ? context + ": " : ""}${errorMessage}`)
    console.error(`Error ${context}:`, error)
  }

  const showSuccessMessage = (text) => {
    message.success(text)
  }

  const showLoadingMessage = (text) => {
    return message.loading(text, 0)
  }

  // Add this direct calculation function
  const calculateStatistics = useCallback(() => {
    // Use the correct selector data instead of raw state
    const practiceAssessmentsData = completedPracticeAssessments || []

    // Calculate practice hours (convert from minutes to hours)
    const totalMinutes = practiceAssessmentsData.reduce((sum, assessment) => {
      const duration = Number(assessment.duration || 0)
      return sum + duration // API returns duration in minutes
    }, 0)

    // Convert minutes to hours and format to 2 decimal places
    const totalHours = totalMinutes / 60

    // Count by assessment type (only practice assessments)
    const communicationCount = practiceAssessmentsData.filter(
      (a) => (a.type || a.assessmentType || "").toLowerCase() === "communication",
    ).length

    const aptitudeCount = practiceAssessmentsData.filter(
      (a) => (a.type || a.assessmentType || "").toLowerCase() === "aptitude",
    ).length

    const technicalCount = practiceAssessmentsData.filter(
      (a) => (a.type || a.assessmentType || "").toLowerCase() === "technical",
    ).length

    const result = {
      totalPracticed: totalHours.toFixed(2), // Convert to hours and format to 2 decimal places
      communicationSessions: communicationCount,
      aptitudeSessions: aptitudeCount,
      technicalSessions: technicalCount,
    }

    return result
  }, [completedPracticeAssessments])

  // Update your dashboard data with the calculated statistics
  useEffect(() => {
    const stats = calculateStatistics()
    setDashboardData(stats)
  }, [calculateStatistics])

  // Add useEffect to fetch aptitude practice scores
  useEffect(() => {
    if (authStudentEmail) {
      dispatch(fetchAptitudePracticeScores(authStudentEmail)).catch((error) => {
        showErrorMessage(error, "Failed to load aptitude practice scores")
      })
    }
  }, [authStudentEmail, dispatch])

  // Enhanced custom tooltip component
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: "white",
            border: "1px solid #d9d9d9",
            borderRadius: "6px",
            padding: "12px",
            boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
          }}
        >
          <p
            style={{
              fontWeight: "600",
              margin: "0 0 8px 0",
              color: "#262626",
              fontSize: "14px",
            }}
          >
            {hasNoPracticeAccess ? `Session ${label}` : `Day ${label}`}
          </p>
          {payload.map((entry, index) => (
            <p
              key={index}
              style={{
                margin: "4px 0",
                color: entry.color,
                fontSize: "13px",
                fontWeight: "500",
              }}
            >
              {`${entry.dataKey}: ${entry.value.toFixed(1)}%`}
            </p>
          ))}
        </div>
      )
    }
    return null
  }

  // Get current month's date range
  const getCurrentMonthRange = (date) => {
    const year = date.getFullYear()
    const month = date.getMonth()
    const startDate = new Date(year, month, 1).toISOString()
    const endDate = new Date(year, month + 1, 0, 23, 59, 59).toISOString()
    return { startDate, endDate }
  }

  // Get month name
  const getMonthName = (date) => {
    return date.toLocaleDateString("en-US", { month: "long", year: "numeric" })
  }

  // Enhanced retry function
  const handleRetry = async () => {
    if (retryLoading) return
    setRetryLoading(true)
    const loadingMsg = showLoadingMessage("Retrying to load data...")

    try {
      const promises = []
      if (authStudentEmail) {
        promises.push(dispatch(fetchPracticeScores(authStudentEmail)))
        promises.push(dispatch(fetchAptitudePracticeScores(authStudentEmail)))
        const { startDate, endDate } = getCurrentMonthRange(currentDate)
        promises.push(dispatch(fetchActivityMap(authStudentEmail, true, startDate, endDate)))
      }
      if (authStudentId) {
        promises.push(dispatch(fetchCompletedPracticeAssessments(authStudentId, 1, 500, null)))
      }

      await Promise.all(promises)
      showSuccessMessage("Data loaded successfully!")
    } catch (error) {
      showErrorMessage(error, "Failed to retry loading data")
    } finally {
      loadingMsg()
      setRetryLoading(false)
    }
  }

  useEffect(() => {
    // Set date range for current month
    const { startDate, endDate } = getCurrentMonthRange(currentDate)
    dispatch(setActivityDateRange(startDate, endDate))

    // Fetch activity data
    const primaryEmail = authStudentEmail
    if (primaryEmail) {
      dispatch(fetchActivityMap(primaryEmail, true, startDate, endDate)).catch((error) => {
        showErrorMessage(error, "Failed to load activity data")
      })
    }
  }, [dispatch, currentDate, authStudentEmail])

  // Add this useEffect to fetch practice scores
  useEffect(() => {
    if (authStudentEmail) {
      dispatch(fetchPracticeScores(authStudentEmail)).catch((error) => {
        showErrorMessage(error, "Failed to load practice scores")
      })
    }
  }, [authStudentEmail, dispatch])

  // Fetch completed practice assessments
  useEffect(() => {
    if (authStudentId) {
      // Fetch ALL practice assessments (not just Communication)
      dispatch(fetchCompletedPracticeAssessments(authStudentId, 1, 500, null)) // null means fetch all types
        .catch((error) => {
          console.error("Failed to fetch completed practice assessments:", error)
          showErrorMessage(error, "Failed to load practice history")
        })
    }
  }, [authStudentId, dispatch])

  // Render Central CTA Card
  const renderCentralCTA = () => (
    <CentralCTAOverlay>
      <CentralCTACard>
        <CTAIcon>
          <LockOutlined />
        </CTAIcon>
        <CTATitle>Unlock Practice</CTATitle>
        <CTADescription>
          Complete the diagnosis assessments assigned to unlock the practice area
        </CTADescription>
        {/* <CTAButton type="primary" size="large">
          Take Assessment
        </CTAButton> */}
      </CentralCTACard>
    </CentralCTAOverlay>
  )

  // Main content component
  const MainContent = () => (
    <>
      {/* Summary Cards */}
      <StatsContainer>
        <SummaryCardsRow gutter={[16, 16]}>
          <Col xs={24} sm={12} lg={6}>
            <SummaryCard bordered={false}>
              {completedPracticeAssessmentsLoading ? (
                <Spin size="small" />
              ) : (
                <Statistic title="Total practiced" value={dashboardData.totalPracticed} suffix="hours" precision={2} />
              )}
            </SummaryCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <SummaryCard bordered={false}>
              {completedPracticeAssessmentsLoading ? (
                <Spin size="small" />
              ) : (
                <Statistic
                  title="Communication sessions"
                  value={dashboardData.communicationSessions}
                  suffix="sessions"
                />
              )}
            </SummaryCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <SummaryCard bordered={false}>
              {completedPracticeAssessmentsLoading ? (
                <Spin size="small" />
              ) : (
                <Statistic title="Aptitude sessions" value={dashboardData.aptitudeSessions} suffix="sessions" />
              )}
            </SummaryCard>
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <SummaryCard bordered={false}>
              {completedPracticeAssessmentsLoading ? (
                <Spin size="small" />
              ) : (
                <Statistic title="Technical sessions" value={dashboardData.technicalSessions} suffix="sessions" />
              )}
            </SummaryCard>
          </Col>
        </SummaryCardsRow>
      </StatsContainer>

      {/* Activity Tracker */}
      <ActivityCard bordered={false} title="Activity Tracker">
        <ActivityContentWrapper>
          <div className="activity-content">
            <TrackerContainer>
              <MonthNavigatorWrapper>
                <MonthNavigator>
                  <MonthButton
                    icon={<LeftOutlined />}
                    onClick={() => navigateMonth(-1)}
                    disabled={monthNavigationLoading}
                    size="small"
                  />
                  <MonthTitle>{getMonthName(currentDate)}</MonthTitle>
                  <MonthButton
                    icon={<RightOutlined />}
                    onClick={() => navigateMonth(1)}
                    disabled={monthNavigationLoading}
                    size="small"
                  />
                </MonthNavigator>
              </MonthNavigatorWrapper>

              {activityLoading && !monthNavigationLoading ? (
                <LoadingWrapper>
                  <Spin size="large" />
                  <span className="loading-text">Loading activity data...</span>
                </LoadingWrapper>
              ) : activityError ? (
                <ErrorWrapper>
                  <div className="error-title">Failed to load activity data</div>
                  <div className="error-description">{activityError}</div>
                  <Button type="primary" icon={<ReloadOutlined />} onClick={handleRetry} loading={retryLoading}>
                    Retry
                  </Button>
                </ErrorWrapper>
              ) : (
                <ActivityTrackerLayout>
                  {/* Left Panel - Calendar Grid */}
                  <CalendarPanel>
                    <CalendarContainer>
                      <WeekdaysGrid>
                        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((day) => (
                          <WeekdayLabel key={day}>{day}</WeekdayLabel>
                        ))}
                      </WeekdaysGrid>
                      <ActivityGrid>
                        {activityData.map((cell, idx) => (
                          <ActivityDayCell
                            key={idx}
                            day={cell.day}
                            isActive={cell.isActive}
                            hasAchievement={cell.hasAchievement}
                            cefr={cell.cefr}
                            isSelected={selectedDate === cell.day && cell.day !== null}
                            onClick={() => !monthNavigationLoading && handleDateSelect(cell)}
                          />
                        ))}
                      </ActivityGrid>
                      <LegendContainer>
                        <LegendItem>
                          <span className="legend-dot"></span>
                          <span>Active days</span>
                        </LegendItem>
                        <LegendItem>
                          <TrophyOutlined className="legend-trophy" />
                          <span>Achievement unlocked</span>
                        </LegendItem>
                      </LegendContainer>
                      <CalendarLoadingOverlay show={monthNavigationLoading}>
                        <Spin size="small" />
                        <div className="loading-text">Loading {getMonthName(currentDate)}...</div>
                      </CalendarLoadingOverlay>
                    </CalendarContainer>
                  </CalendarPanel>

                  {/* Right Panel - Summary Card */}
                  <SummaryPanel>
                    <ActivitySummaryCard>
                      <SummaryHeader>
                        <SummaryDate>
                          {selectedDateData ? (
                            <>
                              Date: {formatSelectedDate(selectedDateData.date)}
                              {isCurrentMonth() && selectedDate === getTodayDate() && (
                                <span
                                  style={{
                                    marginLeft: "8px",
                                    fontSize: "12px",
                                    color: "#40a9ff",
                                    fontWeight: "500",
                                  }}
                                >
                                  (Today)
                                </span>
                              )}
                            </>
                          ) : (
                            <>
                              {isCurrentMonth()
                                ? "Select a date to view activity details"
                                : `${getMonthName(currentDate)} - Select a date to view details`}
                            </>
                          )}
                        </SummaryDate>
                      </SummaryHeader>
                      <SummaryContent>
                        {selectedDateData && selectedDateData.isActive ? (
                          <>
                            {selectedDateData.communicationCount > 0 && (
                              <ActivityItem>
                                <ActivityLabel>Practiced Communication:</ActivityLabel>
                                <ActivityValue>{selectedDateData.communicationCount}</ActivityValue>
                              </ActivityItem>
                            )}
                            {selectedDateData.aptitudeCount > 0 && (
                              <ActivityItem>
                                <ActivityLabel>Practiced Aptitude:</ActivityLabel>
                                <ActivityValue>{selectedDateData.aptitudeCount}</ActivityValue>
                              </ActivityItem>
                            )}
                            {selectedDateData.behaviorCount > 0 && (
                              <ActivityItem>
                                <ActivityLabel>Practiced Behavior:</ActivityLabel>
                                <ActivityValue>{selectedDateData.behaviorCount}</ActivityValue>
                              </ActivityItem>
                            )}
                            {selectedDateData.hasAchievement && (
                              <AchievementBadge>
                                <TrophyOutlined />
                                <span>Communication - Level Up!</span>
                              </AchievementBadge>
                            )}
                          </>
                        ) : selectedDateData ? (
                          <NoActivityMessage>No practice activity recorded for this date</NoActivityMessage>
                        ) : (
                          <NoActivityMessage>
                            {isCurrentMonth()
                              ? "Click on a date to view your practice activity"
                              : "Click on a date to view practice activity for that day"}
                          </NoActivityMessage>
                        )}
                      </SummaryContent>
                    </ActivitySummaryCard>
                  </SummaryPanel>
                </ActivityTrackerLayout>
              )}
            </TrackerContainer>
          </div>
        </ActivityContentWrapper>
      </ActivityCard>

      {/* Practice Progress Summary - Modified to always show chart with demo data when locked */}
      <ProgressCard bordered={false} title="Practice Progress Analytics">
        <ProgressContentWrapper>
          <div className="progress-content">
            {isLoading && !hasNoPracticeAccess ? (
              <LoadingWrapper>
                <Spin size="large" />
                <span className="loading-text">Loading your progress data...</span>
              </LoadingWrapper>
            ) : hasError && !hasNoPracticeAccess ? (
              <ErrorWrapper>
                <div className="error-title">Failed to load progress data</div>
                <div className="error-description">{errorMessage}</div>
                <Button type="primary" icon={<ReloadOutlined />} onClick={handleRetry} loading={retryLoading}>
                  Retry
                </Button>
              </ErrorWrapper>
            ) : !hasChartData && !hasNoPracticeAccess ? (
              <EmptyWrapper>
                <div className="empty-title">No practice data available yet</div>
                <div className="empty-description">Complete some practice assessments to see your progress!</div>
              </EmptyWrapper>
            ) : (
              <>
                <ChartErrorBoundary>
                  <ChartContainer>
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={prepareChartData()} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                        <CartesianGrid stroke="#f0f0f0" strokeDasharray="3 3" />
                        <XAxis
                          dataKey="session"
                          type="number"
                          domain={["dataMin", "dataMax"]}
                          tickCount={Math.min(generateChartData().length, 10)}
                          stroke="#8c8c8c"
                          fontSize={12}
                          tickLine={false}
                          axisLine={{ stroke: "#d9d9d9", strokeWidth: 1 }}
                          label={{ value: 'Days', position: 'insideBottom', offset: -10, style: { textAnchor: 'middle', fill: '#595959', fontSize: 12, fontWeight: 500,fontWeight: 'bold' } }}
                        />
                        <YAxis
                          domain={[0, 100]}
                          stroke="#8c8c8c"
                          fontSize={12}
                          tickLine={false}
                          axisLine={{ stroke: "#d9d9d9", strokeWidth: 1 }}
                           label={{ value: 'Scores', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#595959', fontSize: 12, fontWeight: 500,fontWeight: 'bold' } }}
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Legend
                          verticalAlign="top"
                          align="right"
                          iconType="line"
                          wrapperStyle={{ fontSize: 12, fontWeight: 500 }}
                        />
                        {/* Communication Progress Line */}
                        <Line
                          type="monotone"
                          dataKey="Communication Progress"
                          stroke="#52c41a"
                          strokeWidth={3}
                          dot={{ fill: "#52c41a", strokeWidth: 2, r: 4 }}
                          activeDot={{
                            r: 6,
                            fill: "#52c41a",
                            stroke: "#ffffff",
                            strokeWidth: 2,
                          }}
                          name="Communication Progress"
                          connectNulls={false}
                        />
                        {/* Aptitude Progress Line */}
                        <Line
                          type="monotone"
                          dataKey="Aptitude Progress"
                          stroke="#1890ff"
                          strokeWidth={3}
                          dot={{ fill: "#1890ff", strokeWidth: 2, r: 4 }}
                          activeDot={{
                            r: 6,
                            fill: "#1890ff",
                            stroke: "#ffffff",
                            strokeWidth: 2,
                          }}
                          name="Aptitude Progress"
                          connectNulls={false}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  </ChartContainer>
                </ChartErrorBoundary>
              </>
            )}
          </div>
        </ProgressContentWrapper>
      </ProgressCard>
    </>
  )

  return (
    <SectionWrapper>
      {hasNoPracticeAccess ? (
        <>
          <BlurWrapper>
            <MainContent />
          </BlurWrapper>
          {renderCentralCTA()}
        </>
      ) : (
        <MainContent />
      )}
    </SectionWrapper>
  )
}

export default Overview
